import React, { useCallback, useEffect, useMemo, useState } from "react"
import { connect } from "react-redux"
import { useHistory } from "react-router-dom"

import {
  Card,
  CardActions,
  CardContent,
  Button,
  Modal,
  Backdrop,
  Fade,
} from "@material-ui/core"
import ArrowBackIcon from "@material-ui/icons/ArrowBack"
import CalendarTodayIcon from "@material-ui/icons/CalendarToday"

import { CalendarComponent } from "@syncfusion/ej2-react-calendars"

import { addItem } from "../../redux/cart/cart.actions"
import { selectCartItems } from "../../redux/cart/cart.selectors"

import EN_DATA from "../../data/language/english.data"
import PT_DATA from "../../data/language/portuguese.data"

import api from "../../services/api"

import "./CourseCart.scss"

const BARISTA_COURSE_IDS = [
  "5f8b0c293fb21fbf08d0bc0f",
  "68d587471fbc0c00152d004a",
]

const toInt = (v) => {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

function CourseCart({ course, courses, addItem, language, cartItems }) {
  const history = useHistory()

  const text =
    language === "EN"
      ? EN_DATA.sections.coursecart
      : PT_DATA.sections.coursecart

  const description1 =
    language === "EN" ? course?.description1 : course?.description1_pt
  const description2 =
    language === "EN" ? course?.description2 : course?.description2_pt

  const [freshBaristaCourse, setFreshBaristaCourse] = useState(null)
  const [hide, setHide] = useState(true)
  const [showCalendar, setShowCalendar] = useState(false)
  const [showMessage, setShowMessage] = useState(false)
  const [messageIndex, setMessageIndex] = useState(9)
  const [checking, setChecking] = useState(false)
  const [currentDate, setCurrentDate] = useState(null)
  const [selectedHour, setSelectedHour] = useState("")
  const [spotLeft, setSpotLeft] = useState(0)
  const [bookedSlotKey, setBookedSlotKey] = useState("")

  const toDateOnlyString = useCallback((value) => {
    if (!value) return ""

    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return value
    }

    const d = new Date(value)

    if (Number.isNaN(d.getTime())) return ""

    const year = d.getFullYear()
    const month = String(d.getMonth() + 1).padStart(2, "0")
    const day = String(d.getDate()).padStart(2, "0")

    return `${year}-${month}-${day}`
  }, [])

  const dateOnlyStringToDate = useCallback((value) => {
    if (!value) return null

    if (value instanceof Date) return value

    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [year, month, day] = value.split("-").map(Number)
      return new Date(year, month - 1, day, 12, 0, 0, 0)
    }

    const d = new Date(value)
    return Number.isNaN(d.getTime()) ? null : d
  }, [])

  const formatDate = useCallback((date, label = "") => {
    if (!date) return ""

    if (typeof date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
      const [year, month, day] = date.split("-")
      if (label === "label") return `${day}/${month}`
      return `${day}/${month}/${year}`
    }

    const formDate = new Date(date)

    if (Number.isNaN(formDate.getTime())) return ""

    let day = formDate.getDate()
    let month = formDate.getMonth() + 1
    const year = formDate.getFullYear()

    if (day.toString().length === 1) day = "0" + day
    if (month.toString().length === 1) month = "0" + month

    if (label === "label") return day + "/" + month
    if (label === "database") return year + "-" + month + "-" + day

    return day + "/" + month + "/" + year
  }, [])

  const dayKey = useCallback(
    (d) => {
      if (!d) return ""
      if (typeof d === "string" && /^\d{4}-\d{2}-\d{2}$/.test(d)) return d
      return toDateOnlyString(d)
    },
    [toDateOnlyString],
  )

  const buildCourseDateTimeString = useCallback(
    (dayValue, hourStr) => {
      const day = toDateOnlyString(dayValue)
      if (!day) return ""

      const safeHour = hourStr || "00:00"
      return `${day}T${safeHour}:00`
    },
    [toDateOnlyString],
  )

  const courseBaristaFromProps = useMemo(() => {
    const all = Array.isArray(courses) ? courses : []

    if (course?.is_barista || BARISTA_COURSE_IDS.includes(course?._id)) {
      return course
    }

    const byId = all.find((c) => BARISTA_COURSE_IDS.includes(c?._id))
    if (byId) return byId

    const byFlag = all.find((c) => c?.is_barista)
    if (byFlag) return byFlag

    const byName = all.find(
      (c) => (c?.name || "").trim().toLowerCase() === "barista course",
    )

    return byName || null
  }, [course, courses])

  const courseBarista = freshBaristaCourse || courseBaristaFromProps

  const fetchFreshBaristaCourse = useCallback(async () => {
    const id = courseBaristaFromProps?._id || BARISTA_COURSE_IDS[0]

    if (!id) return null

    try {
      const response = await api.get(`/courses/${id}`)
      const freshCourse = response?.data?.course || null

      if (freshCourse) {
        setFreshBaristaCourse(freshCourse)
        return freshCourse
      }

      return null
    } catch (error) {
      return courseBaristaFromProps || null
    }
  }, [courseBaristaFromProps])

  const hasCalendar = !!(
    courseBarista?.calendar && courseBarista.calendar.length > 0
  )

  const normalizedCalendar = useMemo(() => {
    if (!hasCalendar) return []

    return courseBarista.calendar
      .map((item) => ({
        ...item,
        day: dayKey(item.day),
        hour: String(item.hour || "").trim(),
        limit: toInt(item.limit),
      }))
      .filter((item) => item.day && item.hour)
      .sort((a, b) => `${a.day} ${a.hour}`.localeCompare(`${b.day} ${b.hour}`))
  }, [hasCalendar, courseBarista, dayKey])

  const calendarByDayKey = useMemo(() => {
    const map = new Map()

    normalizedCalendar.forEach((item) => {
      if (!map.has(item.day)) {
        map.set(item.day, [])
      }

      map.get(item.day).push(item)
    })

    return map
  }, [normalizedCalendar])

  const calendarDays = useMemo(() => {
    return Array.from(calendarByDayKey.keys())
      .map((item) => dateOnlyStringToDate(item))
      .filter(Boolean)
  }, [calendarByDayKey, dateOnlyStringToDate])

  const eventSet = useMemo(() => {
    return new Set(calendarByDayKey.keys())
  }, [calendarByDayKey])

  const currentDaySlots = useMemo(() => {
    if (!currentDate?.day) return []

    return calendarByDayKey.get(currentDate.day) || []
  }, [calendarByDayKey, currentDate])

  const selectedSlot = useMemo(() => {
    if (!currentDate?.day || !selectedHour) return null

    return currentDaySlots.find((slot) => slot.hour === selectedHour) || null
  }, [currentDate, currentDaySlots, selectedHour])

  const selectedSlotKey = selectedSlot
    ? `${selectedSlot.day}|${selectedSlot.hour}`
    : ""
  const isBookedForSelectedSlot =
    Boolean(selectedSlotKey) && bookedSlotKey === selectedSlotKey

  useEffect(() => {
    if (!hasCalendar || normalizedCalendar.length === 0) {
      setCurrentDate(null)
      setSelectedHour("")
      setSpotLeft(0)
      return
    }

    const first = normalizedCalendar[0]

    setCurrentDate({
      day: first.day,
    })

    setSelectedHour(first.hour)
  }, [hasCalendar, normalizedCalendar])

  const handleClose = () => setShowCalendar(false)
  const handleMessageClose = () => setShowMessage(false)

  const disabledDate = useCallback(
    (event) => {
      const key = dayKey(event.date)

      if (!eventSet.has(key)) {
        event.isDisabled = true
      }
    },
    [dayKey, eventSet],
  )

  const dateExist = async (dateSelected, _id) => {
    const response = await api.get(`/courses-barista-date`, {
      params: {
        dateSelected: dateSelected.day,
        id: _id,
      },
    })

    return response?.data?.dateExists
  }

  const updateSpotLeft = useCallback(
    async (slotSelected) => {
      if (!courseBarista?._id || !slotSelected?.day || !slotSelected?.hour) {
        setSpotLeft(0)
        return 0
      }

      const limit = toInt(slotSelected.limit)
      let spotsLeft = limit

      try {
        const response = await api.get(
          `/user-courses-dates/${courseBarista._id}`,
        )

        const dates = response?.data || []

        if (dates.length > 0) {
          const selectedDateKey = dayKey(slotSelected.day)

          const bookedCount = dates.filter((el) => {
            if (typeof el === "string") {
              return dayKey(el) === selectedDateKey
            }

            return (
              dayKey(el?.selected_date_key || el?.date || el?.day || el) ===
                selectedDateKey &&
              (!el?.selected_hour_key ||
                String(el.selected_hour_key).trim() === slotSelected.hour)
            )
          }).length

          spotsLeft = limit - bookedCount
        } else {
          const exist = await dateExist(slotSelected, courseBarista._id)
          if (exist) spotsLeft = limit
        }
      } catch (e) {
        spotsLeft = limit
      }

      const safe = Math.max(0, spotsLeft)
      setSpotLeft(safe)
      return safe
    },
    [courseBarista?._id, dayKey],
  )

  useEffect(() => {
    if (selectedSlot) {
      updateSpotLeft(selectedSlot)
    }
  }, [selectedSlot, updateSpotLeft])

  const onChangeDate = useCallback(
    async (event) => {
      if (!hasCalendar) return

      const key = dayKey(event.value)
      const slotsForDay = calendarByDayKey.get(key) || []

      if (slotsForDay.length === 0) return

      const firstSlot = slotsForDay[0]

      setCurrentDate({
        day: key,
      })

      setSelectedHour(firstSlot.hour)

      await updateSpotLeft(firstSlot)
    },
    [hasCalendar, dayKey, calendarByDayKey, updateSpotLeft],
  )

  const openCalendarWithFreshAvailability = useCallback(async () => {
    const freshCourse = await fetchFreshBaristaCourse()
    const calendar = freshCourse?.calendar || courseBarista?.calendar || []

    if (!calendar.length) return

    setShowCalendar(true)
  }, [fetchFreshBaristaCourse, courseBarista])

  const setPriceZero = (c) => ({ ...c, price: 0 })

  const addBaristaLineForSelectedDate = useCallback(() => {
    if (!selectedSlot?.day || !selectedSlot?.hour || !courseBarista) return

    const dateCourse = buildCourseDateTimeString(
      selectedSlot.day,
      selectedSlot.hour,
    )

    const maxQty = Number(spotLeft) || 0

    const productToAdd =
      course?._id === courseBarista?._id ? course : setPriceZero(courseBarista)

    addItem({
      ...productToAdd,
      dateSelected: selectedSlot,
      dateCourse,
      selectedDateKey: selectedSlot.day,
      selectedHourKey: selectedSlot.hour,
      maxQty,
    })

    handleClose()
  }, [
    addItem,
    buildCourseDateTimeString,
    course,
    courseBarista,
    selectedSlot,
    spotLeft,
  ])

  const addFoodSafetyOnlineForSameDate = useCallback(() => {
    const courseId =
      language === "EN"
        ? "60b35888a6e53a00171c7efb"
        : "5f83853c539a937ed63fbf2c"

    const all = Array.isArray(courses) ? courses : []
    const foodSafetyCourse = all.find((c) => c?._id === courseId) || null
    if (!foodSafetyCourse) return

    addItem({
      ...setPriceZero(foodSafetyCourse),
      dateCourse: "",
      maxQty: 1,
    })
  }, [addItem, courses, language])

  const comboCourses = useMemo(
    () =>
      new Set([
        "5f8b0e213fb21fbf08d0bc11",
        "61b4fb459f146a00182881f0",
        "5f8b0eac3fb21fbf08d0bc12",
      ]),
    [],
  )

  const handleClick = useCallback(
    async (selectedCourse) => {
      if (!selectedSlot || checking || isBookedForSelectedSlot) return

      setChecking(true)

      try {
        const newSpotLeft = await updateSpotLeft(selectedSlot)
        if (newSpotLeft <= 0) return

        if (comboCourses.has(selectedCourse._id)) {
          // courseBarista must be loaded before we commit to the combo:
          // addBaristaLineForSelectedDate() no-ops silently without it,
          // which used to let Combo Start reach checkout with no paired
          // Barista Course line (unbooked seat, invisible to capacity checks).
          if (!courseBarista?._id) {
            setMessageIndex(10)
            setShowMessage(true)
            return
          }

          // The companion Barista/Food Safety lines are 1:1 with the combo
          // itself — never re-add them if this exact date/hour is already
          // in the cart, or a repeated click (double-click, or "reservar"
          // again after navigating back) silently bumps their quantity
          // past 1 (Wakana Adachi case, 2026-09).
          const companionAlreadyInCart = cartItems.some(
            (ci) =>
              ci._id === courseBarista._id &&
              ci.selectedDateKey === selectedSlot.day &&
              ci.selectedHourKey === selectedSlot.hour,
          )

          addItem({
            ...selectedCourse,
            dateCourse: "",
            dateSelected: selectedSlot,
            selectedDateKey: selectedSlot.day,
            selectedHourKey: selectedSlot.hour,
            maxQty: Number(newSpotLeft) || 0,
          })

          if (!companionAlreadyInCart) {
            addBaristaLineForSelectedDate()
            addFoodSafetyOnlineForSameDate()
          }

          setBookedSlotKey(selectedSlotKey)
          history.push("/cart")
          return
        }

        if (selectedCourse._id === courseBarista?._id) {
          addBaristaLineForSelectedDate()
          setBookedSlotKey(selectedSlotKey)
          history.push("/cart")
          return
        }

        addItem({ ...selectedCourse, dateCourse: "" })
        addBaristaLineForSelectedDate()
        setBookedSlotKey(selectedSlotKey)
        history.push("/cart")
      } finally {
        setChecking(false)
      }
    },
    [
      addBaristaLineForSelectedDate,
      addFoodSafetyOnlineForSameDate,
      addItem,
      cartItems,
      checking,
      comboCourses,
      courseBarista?._id,
      isBookedForSelectedSlot,
      selectedSlot,
      selectedSlotKey,
      history,
      updateSpotLeft,
    ],
  )

  const needsBaristaDateSelection = Boolean(
    hasCalendar &&
    courseBarista &&
    (course?._id === courseBarista?._id || course?.is_combo),
  )

  const canBuy = course?.price !== 0

  return (
    <Card className="card">
      <CardContent>
        <h3 className="card-title txt-border">{course?.name}</h3>

        {course?._id === courseBarista?._id && hasCalendar ? (
          <button
            type="button"
            className="card-days"
            onClick={() => {
              openCalendarWithFreshAvailability()
            }}
          >
            <h4>
              <u>{text[1]}</u>
            </h4>
            <CalendarTodayIcon />
          </button>
        ) : null}

        {showCalendar ? (
          <Modal
            aria-labelledby="modal-title-calendar"
            aria-describedby="modal-description-calendar"
            className="item-modal"
            open={showCalendar}
            onClose={handleClose}
            closeAfterTransition
            BackdropComponent={Backdrop}
            BackdropProps={{ timeout: 500 }}
          >
            <Fade in={showCalendar}>
              <div className="paper-calendar">
                <div className="modal-label">
                  <span></span>
                  <label>{text[3]}</label>
                </div>

                <div id="modal-description-calendar">
                  <CalendarComponent
                    id="calendar"
                    change={onChangeDate}
                    renderDayCell={disabledDate}
                    values={calendarDays}
                  />
                </div>

                {currentDate ? (
                  <div className="paper-label">
                    <div className="paper-date">
                      <label>
                        <b>{formatDate(currentDate.day, "label")}</b>
                      </label>
                    </div>

                    <div className="paper-description">
                      <label>
                        <b>Barista Course</b>
                      </label>

                      {currentDaySlots.length > 1 ? (
                        <div className="hour-options">
                          {currentDaySlots.map((slot) => (
                            <button
                              type="button"
                              key={`${slot.day}-${slot.hour}`}
                              className={
                                selectedHour === slot.hour ? "active" : ""
                              }
                              onClick={() => setSelectedHour(slot.hour)}
                            >
                              {slot.hour}
                            </button>
                          ))}
                        </div>
                      ) : null}

                      <div className="paper-buy">
                        <div>
                          {text[4]} <b>{selectedSlot?.hour}</b>
                        </div>

                        {spotLeft > 0 ? (
                          <div>
                            <button
                              type="button"
                              className="paper-click"
                              onClick={() => handleClick(course)}
                              disabled={checking || isBookedForSelectedSlot}
                            >
                              <b>
                                <u>
                                  {checking
                                    ? "..."
                                    : isBookedForSelectedSlot
                                    ? text[11]
                                    : text[5]}
                                </u>
                              </b>
                            </button>

                            <div className="paper-left">
                              {text[6]} {spotLeft} {text[7]}
                            </div>
                          </div>
                        ) : (
                          <div className="paper-left">{text[8]}</div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="paper-label">
                    <div className="paper-left">{text[8]}</div>
                  </div>
                )}
              </div>
            </Fade>
          </Modal>
        ) : null}

        <Modal
          aria-labelledby="modal-title"
          aria-describedby="modal-description"
          className="item-modal"
          open={showMessage}
          onClose={handleMessageClose}
          closeAfterTransition
          BackdropComponent={Backdrop}
          BackdropProps={{ timeout: 500 }}
        >
          <Fade in={showMessage}>
            <div className="paper">
              <h2 id="modal-title">{text[messageIndex]}</h2>
            </div>
          </Fade>
        </Modal>

        <div className="card-description">
          <div>
            <p>{description1}</p>

            {description2 ? (
              <div className={`card-link ${hide ? "" : "hide"}`}>
                <button type="button" onClick={() => setHide(false)}>
                  {text[0]}
                </button>
              </div>
            ) : null}
          </div>

          <div className={`${hide ? "hide" : ""}`}>
            <p>{description2}</p>
            <div className="card-link">
              <button type="button" onClick={() => setHide(true)}>
                <ArrowBackIcon />
              </button>
            </div>
          </div>
        </div>

        {canBuy ? <div className="card-price">€{course.price}</div> : null}
      </CardContent>

      <CardActions className="btn-container">
        {canBuy ? (
          <Button
            className="btn-orange"
            disabled={checking}
            onClick={() => {
              if (needsBaristaDateSelection) {
                openCalendarWithFreshAvailability()
                return
              }

              addItem({ ...course, dateCourse: "" })
              history.push("/cart")
            }}
          >
            {text[2]}
          </Button>
        ) : null}
      </CardActions>
    </Card>
  )
}

const mapStateToProps = (state) => ({
  cartItems: selectCartItems(state),
})

const mapDispatchToProps = (dispatch) => ({
  addItem: (item) => dispatch(addItem(item)),
})

export default connect(mapStateToProps, mapDispatchToProps)(CourseCart)
